
import {catchError, filter, map, take, timeout} from 'rxjs/operators';
import {Injectable} from "@angular/core";
import {Observable, of, Subject, throwError} from "rxjs";
import {
    AssetId,
    ProjectId,
    ArtifactId,
    StateDataId,
    BrowserSessionStateId,
    FactSessionId,
    DeviceId,
    ExpeditionId,
    StateCollectorJobId,
    StateCollectionId,
    TestTemplateId,
    StateCollectorSpecId,
    UserId
} from '../../types/ids';
import {Browser, browserFromBackendObject} from '../../types/browser';
import {AuthService} from "../../services/auth.service";
import {BrowserSessionInfo, State, StateDataInfo} from '../../types/browsersession';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {FactRequest, FactType, LiveExpeditionSpec, NewStateCollectorJobSpec, StateCollection, StateCollectorJob, StateCollectorSpec, TestExecutionSummary} from "@webmate-web-core/shared/types/testmgmt";
import {TestService} from "@webmate-web-core/shared/services/test.service";
import {ArtifactService} from "@webmate-web-core/shared/services/artifact.service";
import {ApiSourcedPaginationRequest, PaginatedResult, QueryMetadata, queryMetadataToQueryParams} from "@webmate-web-core/shared/api/apibase.api";
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {BaseUrlSkipHeader} from '@webmate-web-core/shared/constants/interceptor-custom-headers.constants';

export type BrowserDto = Browser;

export interface BrowserSessionInfoDto {
    id: ExpeditionId,
    browser: BrowserDto,
    sessionState: string,
    user: UserId,
    projectId: ProjectId,
    optStart: DateTime | null,
    optEnd: DateTime | null,
    optDeviceId?: DeviceId,
    startupFactSessionId?: FactSessionId | null
}

export type StateDto = State; // StateDto has no screenshot url. Otherwise, everything is equal to State

export interface StateCreationInfo {
    stateId: BrowserSessionStateId;
    factSessionId: FactSessionId;
}

// TODO find an actual types?
export type StateDataDomStructure = any;
export type StateDataDomNode = any;


/**
 * REST Access to webmate BrowserSession API.
 */
@Injectable({
  providedIn: "root"
})
export class BrowserSessionApi {

    static readonly BROWSERSESSIONBULK_TIMEOUT_MILLIS = 60000;
    static readonly BROWSERSESSIONBULK_REQUEST_BATCHINTERVAL = 500;
    static readonly MAX_BROWSERSESSIONS_IN_ONE_BULKREQUEST = 30;

    latestBrowserSessionBulkRequestBatch: ArtifactId[] = [];

    latestBrowserSessionBulkResponses = new Subject<BrowserSessionInfoDto>();

    constructor(public OAuth2AuthService: AuthService, private http: HttpClient, private webmateOptions: WebmateOptions) {
        let self = this;
        setTimeout(() => self.retrieveBatchedBrowserSessions(self), 1000);
    }

    getBrowserSessionInfo(id: ExpeditionId): Observable<BrowserSessionInfoDto> {

        this.latestBrowserSessionBulkRequestBatch = [...this.latestBrowserSessionBulkRequestBatch, id];

        // only listen for the first session with the same id that you have requested
        return this.latestBrowserSessionBulkResponses.asObservable().pipe(
            filter(session => session.id == id),
            take(1),
            timeout(BrowserSessionApi.BROWSERSESSIONBULK_TIMEOUT_MILLIS),
            catchError(err => throwError(`Requesting browsersession ${id} has timed out after ${BrowserSessionApi.BROWSERSESSIONBULK_TIMEOUT_MILLIS}`))
        );
    }

    getBrowserSessionStates(id: ExpeditionId): Observable<BrowserSessionStateId[]> {
        return this.http.get<ExpeditionId[]>(`/browsersession/${id}/states`).pipe(map(arr => arr ?? []));
    }

    analyzeState(expedition: ExpeditionId, requestedFactTypes?: FactRequest[], async: boolean = false): Observable<BrowserSessionStateId> {
        return this.http.post<BrowserSessionStateId>(
            `/browsersession/${expedition}/states`,
            this.stateExtractionRequest(requestedFactTypes, async)
        );
    }

    createStateV2(expedition: ExpeditionId, requestedFactTypes: FactRequest[]): Observable<StateCreationInfo> {
        const headers = new HttpHeaders().set(BaseUrlSkipHeader, '');
        return this.http.post<StateCreationInfo>(
            `${this.webmateOptions.baseApiUri}/v2/browsersession/${expedition}/states`,
            this.stateExtractionRequest(requestedFactTypes, true),
            {headers}
        );
    }

    mapSessionArtifactsToStates(id: ExpeditionId): Observable<any> {
        return this.http.get<any>("/browsersession/" + id + "/states-with-artifacts");
    }

    getStateDataInfo(id: StateDataId): Observable<StateDataInfo> {
        return this.http.get<StateDataInfo>("/statedata/" + id + "/info");
    }

    getStateDataDomStructure(id: StateDataId): Observable<StateDataDomStructure> {
        return this.http.get<StateDataDomStructure>("/statedata/" + id + "/domstructure");
    }

    getStateDataDomNode(id: StateDataId, nodeId: string): Observable<StateDataDomNode> {
        return this.http.get<StateDataDomNode>("/statedata/" + id + "/domnodes/" + nodeId);
    }

    retrieveBatchedBrowserSessions(self: BrowserSessionApi): void {
        if (self.latestBrowserSessionBulkRequestBatch.length > 0) {
            let requestsToBeRetrieved = self.latestBrowserSessionBulkRequestBatch.slice(0, BrowserSessionApi.MAX_BROWSERSESSIONS_IN_ONE_BULKREQUEST);
            self.latestBrowserSessionBulkRequestBatch = self.latestBrowserSessionBulkRequestBatch.slice(BrowserSessionApi.MAX_BROWSERSESSIONS_IN_ONE_BULKREQUEST);
            this.http.get<any[]>("/browsersession/bulk/info?ids=" + requestsToBeRetrieved.join(",")).subscribe(result => {
                result.forEach(session => {
                    session.browser = browserFromBackendObject(session.browser);
                    session.associatedStates = session.associatedStates ?? [];
                    return self.latestBrowserSessionBulkResponses.next(session as BrowserSessionInfoDto);
                });
            });
        }
        setTimeout(() => self.retrieveBatchedBrowserSessions(self), BrowserSessionApi.BROWSERSESSIONBULK_REQUEST_BATCHINTERVAL);
    }

    startExpedition(projectId: ProjectId, liveExpeditionSpec: LiveExpeditionSpec): Observable<ExpeditionId> {
        console.log('starting expedition', liveExpeditionSpec);
        return this.http.post<ExpeditionId>(`/projects/${projectId}/expeditions`, liveExpeditionSpec);
    }

    stopExpedition(expeditionId: ExpeditionId): Observable<void> {
        return this.http.post<void>(`/browsersession/${expeditionId}/stop`, {});
    }

    startStateCollectorJob(projectId: ProjectId, collectorSpec: NewStateCollectorJobSpec): Observable<StateCollectorJobId> {
        const bySpec = {
            spec: collectorSpec
        }; // in the backend, you can alternatively provide a specId

        return this.http.post<StateCollectorJobId>(`/projects/${projectId}/statecollectorjobs`, bySpec);
    }

    getStateCollectorJob(jobId: StateCollectorJobId): Observable<StateCollectorJob> {
        return this.http.get<StateCollectorJob>(`/expedition/statecollectorjobs/${jobId}`);
    }

    queryStateCollectorJobs(projectId: ProjectId, queryMetadata: QueryMetadata): ApiSourcedPaginationRequest<StateCollectorJob> {
        return new ApiSourcedPaginationRequest<StateCollectorJob>(`/projects/${projectId}/statecollectorjobs`, this.http, queryMetadataToQueryParams(queryMetadata));
    }

    getStateCollection(id: StateCollectionId): Observable<StateCollection> {
        return this.http.get<StateCollection>(`/expedition/statecollections/${id}`);
    }

    getStateCollections(projectId: ProjectId): Observable<StateCollection[]> {
        return this.http.get<StateCollection[]>(`/projects/${projectId}/statecollections`);
    }

    getStateCollectorSpec(id: StateCollectorSpecId): Observable<StateCollectorSpec> {
        return this.http.get<StateCollectorSpec>(`/expedition/statecollectorspecs/${id}`);
    }

    getExpeditionsInProject(projectId: ProjectId, queryMetadata: QueryMetadata): ApiSourcedPaginationRequest<BrowserSessionInfo> {
        return new ApiSourcedPaginationRequest<BrowserSessionInfo>(`/projects/${projectId}/expeditions`, this.http, queryMetadataToQueryParams(queryMetadata), BrowserSessionInfo.fromBackendObject);
    }

    interact(expeditionId: ExpeditionId, interactionPath: any): Observable<void> {
        return this.http.post<void>(`/browsersession/${expeditionId}/interact`, interactionPath);
    }

    private stateExtractionRequest(requestedFactTypes: FactRequest[] | undefined, async: boolean): object {
        return {
            extractionConfig: {
                ...(requestedFactTypes
                    ? {requestedFacts: requestedFactTypes.map(type => ({factType: type.factType, params: type.params}))}
                    : {}),
                async
            }
        };
    }

}
